import { getUserData } from './API';
import device from './device/device';
import { getActivityStatus } from './displayStatus';
import {userTypes} from "Constants/constants";
import versionCompare from 'semver-compare';
import * as Sentry from '@sentry/browser';
import { isiPad } from './environment';

export function promiseTimeout(promise, ms){

  // Create a promise that rejects in <ms> milliseconds
  let timeout = new Promise((resolve, reject) => {
    let id = setTimeout(() => {
      clearTimeout(id);
      reject(new Error('Timed out in '+ ms + 'ms.'));
    }, ms)
  })

  // Returns a race between our timeout and the passed in promise
  return Promise.race([
    promise,
    timeout
  ])
}
export function formatAREAScore(score, long=false, veryshort=false){ // TODO pass these props more cleanly
  let years = Math.floor(score);
  let months = Math.round((score - years) * 12);
  if(long){
    return (years + " year, " + months + " month");
  }
  if(veryshort) {
    return(years + "y, " + months + "m");
  }
  return(years + " y, " + months + " m");
}

// TODO dedup this function
export function uuidToHEX(value){
  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if(pattern.test(value)){
    value = value.replace(/\-/g, '').toUpperCase();
  }
  return value;
}

export function formatGuidForSIS(guid){
  let formattedGuid = guid.toLowerCase();

  //Check for valid UUID and fix if not
  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if(pattern.test(formattedGuid) === false){
    formattedGuid = formattedGuid.replace(/(\w{8})(\w{4})(\w{4})(\w{4})(\w{12})/, "$1-$2-$3-$4-$5");
  }
  return formattedGuid;
}

export function makeStringPosessive(nameString){
  return nameString + (nameString[nameString.length -1] == 's' ? "'" : "'s");
}

//arrToSort - [Array]
//asc - [Boolean]
// order the activities by status. Completed first, then errors, then processing, then assigned
export function sortByStatus(arrToSort, asc){
  let result = arrToSort.sort((element1, element2) => {
    let status1 = element1.assessmentStatus ? element1.assessmentStatus.id : getActivityStatus(element1).id;
    let status2 = element2.assessmentStatus ? element2.assessmentStatus.id : getActivityStatus(element2).id;
    if (status1 == status2) {
      if(element1.updatedAt && element2.updatedAt){
        return element2.updatedAt - element1.updatedAt;
      }else if(element1.date && element2.date) {
        return new Date(element1.date).getTime() - new Date(element2.date).getTime();
      }
    } else if(status1 == "ERROR"){
      return -1;
    } else if(status1 == "ABORTED" && status2 != "ERROR"){
      return -1;
    } else if(status1 == "COMPLETE" && status2 != "ERROR" && status2 != "ABORTED"){
      return -1;
    } else if(status1 == "RESCORED" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE"){
      return -1;
    } else if(status1 == "SCORING" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED"){
      return -1;
    } else if(status1 == "IN_PROGRESS" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED" && status2 != "SCORING"){
      return -1;
    } else if(status1 == "ASSIGNED" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED" && status2 != "SCORING" && status2 != "IN_PROGRESS"){
      return -1;
    } else if(status1 == "UNASSESSED" &&  status2 != "ERROR" && status2 != "ABORTED" && status2 != "COMPLETE" && status2 != "RESCORED" && status2 != "SCORING" && status2 != "IN_PROGRESS" && status2 != "ASSIGNED"){
      return -1;
    } else {
      return 1;
    }
  });

  return asc ? result : result.reverse();
}

/**
 * Takes a string, stripes the <speak>, <prosody> tags, then create a array of objects in the form of { type, value } to be used for speech.
 * @param {string} speechString A string to be spoken, only strings containing <phoneme voice="human" ... /> will be processed.
 * @returns An array of objects in the form of { type, value }, returns an empty array if there is no Phonemes in the string.
 */
export function parseTextWithPhonemes(speechString){

    if(!stringContainsHumanPhoneme(speechString)) return []

    const phonemeRegex = /(<phoneme voice="human" alphabet="ipa" ph="['ˈa-zA-Z\u0250-\u02AF\/\æ\ð\ŋ\θ]+"\/>)/g
    const phonemeValueRegex = /(<phoneme voice="human" alphabet="ipa" ph="(['ˈa-zA-Z\u0250-\u02AF\/\æ\ð\ŋ\θ]+)"\/>)/g // with an inner group for the phoneme value

    // clean up the string by removing <speak>, <prosody> tags
    const removedSpeakTags = removeSpeakTags(speechString)
    const removedSpeedTags = removeProsodyTags(removedSpeakTags)
    const splitSpeechString = removedSpeedTags.split(phonemeRegex);

    // construct an array of object with property { type, value }
    const array = []
    while(splitSpeechString.length){

      const item = splitSpeechString.shift().trim()
      if(!item) continue // ignore empty strings
      if(!removeBreakTags(item).trim().length) continue // ignore string with only break tags
      // --- possibly need to ignore some other tags as the only thing in the string ---

      const isPhoneme = item.match(phonemeRegex)

      const phonemeValue = [...item.matchAll(phonemeValueRegex)][0]
      array.push({
        type: isPhoneme ? "phoneme" : "text",
        value: isPhoneme ? phonemeValue[2] : `<speak>${removeLeadingPunctuation(item)}</speak>`
      })
    }
    return array
}

/**
 * Used to check if a string contains a correctly formatted phoneme tag to use human voice,
 * a phoneme for human voice string has a strict format, the order of the attributes matters.
 * @param {string} speechString a string to check for phoneme to use human voice
 * @returns the match or null
 */
export function stringContainsHumanPhoneme(speechString){
  const humanPhonemeRegex = /(<phoneme voice="human" alphabet="ipa" ph="['ˈa-zA-Z\u0250-\u02AF\/\æ\ð\ŋ\θ]+"\/>)/g
  return speechString ? speechString.match(humanPhonemeRegex) : null;
}

/**
 * Used to clean up a string with leading punctuation and space characters
 * @param {string} string any string
 * @returns
 */
export function removeLeadingPunctuation(string){
  const nonWordCharRegex = /^[\.\,\!\?\s]*/g
  return string.replace(nonWordCharRegex, '')
}

/**
 * Used to remove any <speak>...</speak> tags from a string
 * @param {string} string any string
 * @returns string without the <speak> tags
 */
export function removeSpeakTags(string){
  const speakTagRegex = /<\/*speak>/g
  return string.replace(speakTagRegex, "");
}

/**
 * Used to remove any <prosody>...</prosody> tags from a string
 * @param {string} string any string
 * @returns string without the prosody tags
 */
export function removeProsodyTags(string){
  const prosodyTagRegex = /<\/?prosody ?(rate\=\"\w+\")?>/g
  return string.replace(prosodyTagRegex, "")
}

/**
 * Used to remove any <break/> tags from a string
 * @param {string} string any string
 * @returns string without the break tags
 */
export function removeBreakTags(string){
  const breakTagRegex = /<\/?break ?(time\=\"[0-9]+.[0-9]+s?\")?\/>/g
  return string.replace(breakTagRegex, "")
}

export function parseSkipTranslationTags(speechString){
  let cleanSpeechString = speechString.replace(/<\/*speak>/g, "");
  let splitSpeechString = cleanSpeechString.split(/<mark [a-zA-Z|\s|=|"|:]*skipTranslation[a-zA-Z|\s|=|"|:]*\/>/g);
  let parsedSpeechText = [];
  for(let i = 0; i < splitSpeechString.length; i++){
    let textToSpeak = splitSpeechString[i];
    if(textToSpeak && textToSpeak != ""){
      parsedSpeechText.push({
        textToSpeak: textToSpeak,
        skipTranslation: i%2 == 1,
      })
    }
  }
  return parsedSpeechText;
}

// takes an array of activity objects and returns that array sorted by date created, most recent first
export function sortActivitiesByDate(activities){
  let compare = function (activity1,activity2) {
    let date1 = new Date(activity1.updatedAt);
    let date2 = new Date(activity2.updatedAt);
    if (date1.getTime() < date2.getTime()){
      return 1;
    } else if (date1.getTime() > date2.getTime()){
      return -1;
    } else {
      return 0;
    }
  }
  return activities.sort(compare);
}

//sort activities by descending createdAt date
export function sortActivitiesByCreatedDate(activities){
  return activities.sort((a, b) => {
    const aDate = new Date(a.createdAt || 0);
    const bDate = new Date(b.createdAt || 0);
    return bDate - aDate;
  });
}

export function formatAsOrdinal(i) {
  var j = i % 10,
      k = i % 100;
  if (j == 1 && k != 11) {
      return i + "st";
  }
  if (j == 2 && k != 12) {
      return i + "nd";
  }
  if (j == 3 && k != 13) {
      return i + "rd";
  }
  return i + "th";
}

export function containsNonAlpha(str){
  return !/^[a-z]+$/i.test(str);
}

export function isAdminUser(userType) {
  return userType === userTypes.DISTRICT_ADMIN || userType === userTypes.SCHOOL_ADMIN;
}

export function isBrowser() {
  // window.device is not always defined immediately on application launch on iOS.
  // This was moved from a constant in constants.js to a computed value in util.js so that it would only be accessed after the deviceready event in index.js.
  return !window.device || window.device.platform === 'browser'; // TODO: Move this to an env var
}

export function isCordova(){
  return window.cordova != null;
}

export function delay(duration) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

export function getSentryEnv(){
  if(!process.env.STACK) return "development"
  // To lower case to make sure we don't create a new production environment in sentry.
  return process.env.STACK.toLocaleLowerCase()
}

export function logSentryException(error, infoMessage) {
  // First, check if error is an Error object. If not, then create a new error with the infoMessage.
  // If using the infoMessage, check if the error objects passed in is a graphql type error and log that data in a breadcrumb
  if (error instanceof Error || (error && error.stack && error.message)) {
    if (infoMessage) {
      Sentry.addBreadcrumb({
        message: infoMessage,
        level: "error",
      });
    }
    Sentry.captureException(error);
  } else {
    if (error && error.errors) {
      const data = {
        graphqlErrors: JSON.stringify(error.errors),
      }

      if (error.data) {
        data.graphqlData = JSON.stringify(error.data);
      }

      Sentry.addBreadcrumb({
        message: 'GraphQL error',
        level: "error",
        data,
      });
    }
    
    Sentry.captureException(new Error(infoMessage || 'Unknown error'));
  }
}

export function isNullOrUndefined(value) {
  return value === null || value === undefined;
}

export function isNotNullOrUndefined(value) {
  return value !== null && value !== undefined;
}

/**
 * Howler defaults to Web Audio API and falls back to HTML5 Audio if it is not supported.
 * Versions that do not have Web Audio API support
 * Pre-iOS 14.5 (Good with Howler automatic fallback to HTML5 audio)
 * Versions that have problem with HTML5 audio
 * iOS 15.4 (Good with Howler default with Web Audio API)
 * Versions that have problem with Web Audio API (AudioContext)
 * iOS 16.4.1 - 16.5 current as of June 2023 (Implemented Howler force HTML5 audio)
 * TODO: Limit forcing HTML5 audio when iOS fixes the Web Audio API bug by limiting to the fixed version.
 * https://docs.google.com/document/d/1vB2AHqNoBfizvZ3WGrwjE_GynlRdVb7YnILejHxRYuQ/edit#heading=h.sdghvqz7lm5d
 */
export function shouldForceHTML5Audio(){
   const version = device.getBrowserForSession().version;
   const isIOS16_4_or_newer = isiPad() && (versionCompare(version, "16.4.1") >= 0);
   const forceHTML5 = isIOS16_4_or_newer;
   return forceHTML5;
}

export function iOSSupportsAudioSession() {
  const version = device.getBrowserForSession().version;
  return isiPad() && (versionCompare(version, "16.4.0") >= 0);
}

/**
 * Checks wheether an email is valid
 * @param {string} email email to check
 * @returns {boolean} valid or not
 */
export function isValidEmail(email) {
  const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return regex.test(String(email).toLowerCase());
}